/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Row, Typography } from 'antd';
import { LinkOutlined, UserOutlined } from '@ant-design/icons';
import { ChatBotIcon } from '../../../Icons/ChatBotIcon';
import ColorPicker from '../../Fields/ColorPicker';

// Misc imports
import userHasEditAccess from '../../../../utils/misc/hasEditAccess';
import useTheme from '../../../../utils/hooks/useTheme';
import { isColorHex } from '../../../../utils/is/isColorHex';

// Constant declarations
const FORM_CONFIGURATION_LAYOUT_PATH = ['settings', 'layout', 'microsoftWebChat', 'configuration'];

const BotDeploymentsWebchatColorsForm = ({
  formData = {},
  userRoles = {},
  isCreateMode = false,
  form
}) => {

  const { t } = useTranslation();
  const { formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);

  return (
    <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
      <Col xs={formProps.mainColSpan}>
        <Typography.Text>
          {t('Colors')}
        </Typography.Text>

        <Typography.Paragraph className="fs-sm" type="secondary">
          {t('Make your agent stand out with colors that match your brand.')}
        </Typography.Paragraph>
      </Col>

      <Col xs={formProps.secondaryColSpan}>
        <Form.Item
          label={t('Main Color')}
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'mainColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#605DEC" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'mainColor'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={t('Main Font Color')}
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'mainFontColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#ffffff" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'mainFontColor'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={
            <Typography.Text>
              <LinkOutlined /> {t('Hyperlink Color')}
            </Typography.Text>
          }
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'hyperlinkColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#2196f3" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'hyperlinkColor'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={
            <Typography.Text>
              <ChatBotIcon /> {t('Agent Bubble Color', { context: 'AI agent' })}
            </Typography.Text>
          }
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'botBubbleColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#EFEFFD" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'botBubbleColor'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={
            <Typography.Text>
              <ChatBotIcon /> {t('Agent Bubble Text Color', { context: 'AI agent' })}
            </Typography.Text>
          }
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'botBubbleTextColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#626988" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'botBubbleTextColor'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={
            <Typography.Text>
              <UserOutlined /> {t('User Bubble Color')}
            </Typography.Text>
          }
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'userBubbleColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#626988" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'userBubbleColor'])}</Typography.Text>
          }
        </Form.Item>

        <Form.Item
          label={
            <Typography.Text>
              <UserOutlined /> {t('User Bubble Text Color')}
            </Typography.Text>
          }
          name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'userBubbleTextColor']}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (!isColorHex(value)) {
                  throw new Error(t('Please enter a valid color hex.'));
                }
              }
            }
          ]}
        >
          {
            hasEditAccess
              ? <ColorPicker defaultColor="#FFFFFF" />
              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'userBubbleTextColor'])}</Typography.Text>
          }
        </Form.Item>
      </Col>
    </Row>
  );
};

BotDeploymentsWebchatColorsForm.propTypes = {
  formData: PropTypes.object,
  isCreateMode: PropTypes.bool,
  userRoles: PropTypes.object,
  form: PropTypes.object
};

export default BotDeploymentsWebchatColorsForm;
